import { useMemo } from 'react';
import {
  formatCurrency,
  formatDate,
  formatShortDate,
  formatWorkLocation,
} from '~/features/market/job-opportunities/utils';
import {
  formatMoney,
  formatNegotiationHistoryTerm,
  formatRate,
  JOB_TERM_GROUPS,
  negotiationHistoryTermLabel,
} from '~/features/market/negotiations/negotiationHistory';
import { formatStatusLabel } from '~/features/shared/market-content';
import {
  NegotiationHistory,
  negotiationParty,
  type NegotiationHistoryVersion,
} from '~/features/shared/market-content/NegotiationHistory';
import type { ServiceOffer, ServiceOpportunity } from './schemas';

export const INQUIRY_NOTE = 'Inquiry — no application or offer submitted';

const offersOf = (opportunity: ServiceOpportunity) =>
  opportunity.negotiationHistory ??
  (opportunity.latestNegotiation ? [opportunity.latestNegotiation] : []);

/** Shared wording for an offer's identity, kept in one place so the history
 * and the compact message reference never drift apart. */
export const offerLabel = (offer: ServiceOffer) =>
  `${offer.version === 1 ? 'Initial offer' : 'Counteroffer'} version ${offer.version}`;

const RATE_TERMS = {
  HOUR: { key: 'pricePerHour', unit: 'hr' },
  SHIFT: { key: 'pricePerShift', unit: 'shift' },
  RVU: { key: 'pricePerRvu', unit: 'RVU' },
} as const;
/** The offer's headline rate in its own compensation unit, to the cent (MCV-1794). */
export const offerRate = (offer: ServiceOffer) => {
  const comp = RATE_TERMS[offer.terms.compType as keyof typeof RATE_TERMS] ?? RATE_TERMS.HOUR;
  return formatRate(
    comp.key === 'pricePerHour' ? offer.pricePerHour : offer.terms[comp.key],
    comp.unit
  );
};
const offerShifts = (offer: ServiceOffer) => {
  const shifts = offer.terms.numOfShifts;
  return shifts == null || shifts === '' ? null : String(shifts);
};
const offerValue = (offer: ServiceOffer) => {
  const value = offer.terms.totalContractValue;
  return formatCurrency(value == null ? null : String(value));
};

const SHIFT_TYPES = { SHIFT: 'Shift', CALL: 'Call shift' } as const;

const selectionTimes = (selection: ServiceOffer['availabilitySelection'][number]) => {
  if (!selection.startsAt && !selection.endsAt) return null;
  const clock = (value: string | null) => {
    if (!value) return null;
    const options = { hour: 'numeric', minute: '2-digit' } as const;
    try {
      return new Date(value).toLocaleTimeString('en-US', {
        ...options,
        timeZone: selection.timezone,
      });
    } catch {
      return new Date(value).toLocaleTimeString('en-US', options);
    }
  };
  // [MCV-1794] The times are converted into the selection's own zone, so name it: without the
  // zone, "10:00 AM" reads as the viewer's local time. The abbreviation follows the start (or the
  // end, when only that exists) so a daylight-saving date shows EDT, not EST. An invalid zone
  // falls back to the stored string rather than hiding which zone was meant.
  const zoneName = () => {
    const at = selection.startsAt ?? selection.endsAt;
    try {
      return (
        new Intl.DateTimeFormat('en-US', { timeZone: selection.timezone, timeZoneName: 'short' })
          .formatToParts(new Date(at as string))
          .find((part) => part.type === 'timeZoneName')?.value ?? selection.timezone
      );
    } catch {
      return selection.timezone;
    }
  };
  const range = [clock(selection.startsAt), clock(selection.endsAt)].filter(Boolean).join(' - ');
  return [range, zoneName()].filter(Boolean).join(' ');
};

// An override that is not a number prints as sent, still with its dollar sign.
const overrideAmount = (value: string) =>
  value.trim() !== '' && Number.isFinite(Number(value)) ? formatMoney(value) : `$${value}`;

/** Every rate override on the date; an offer can carry more than one. */
const selectionOverrides = (selection: ServiceOffer['availabilitySelection'][number]) =>
  [
    [selection.pricePerHourOverride, 'hr'] as const,
    [selection.pricePerShiftOverride, 'shift'] as const,
    [selection.pricePerCallShiftOverride, 'call shift'] as const,
  ].flatMap(([value, unit]) => (value ? [`${overrideAmount(value)}/${unit}`] : []));

/** The offer's selected dates in one line, with each date's time and shift on request. */
export const ServiceAvailabilitySummary = ({
  offer,
  open = false,
}: {
  offer: ServiceOffer;
  open?: boolean;
}) => {
  if (offer.selectionStatus === 'UNAVAILABLE') {
    return (
      <p role="status" className="text-sm text-content-primary">
        The complete availability selection is unavailable. Do not accept this offer until it can be
        reviewed.
      </p>
    );
  }
  const dates = offer.availabilitySelection.map((selection) => selection.date).sort();
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return null;
  const range =
    first === last
      ? formatDate(first)
      : first.slice(0, 4) === last.slice(0, 4)
        ? `${formatShortDate(first)} - ${formatDate(last)}`
        : `${formatDate(first)} - ${formatDate(last)}`;
  return (
    <section aria-label="Selected availability" className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
        Selected availability
      </h4>
      <p className="text-sm font-medium text-content-primary">
        {dates.length} {dates.length === 1 ? 'date' : 'dates'}: {range}
      </p>
      <details open={open}>
        <summary className="cursor-pointer text-sm font-semibold text-primary-50">
          {dates.length === 1 ? 'Show date detail' : 'Show dates'}
        </summary>
        <ul className="mt-2 space-y-1 text-sm text-content-primary">
          {[...offer.availabilitySelection]
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((selection, index) => {
              const parts = [
                selectionTimes(selection) ??
                  (selection.detailMode === 'DATE_ONLY' ? 'Date only' : null),
                selection.shiftLabel ? formatStatusLabel(selection.shiftLabel) : null,
                selection.shiftType ? SHIFT_TYPES[selection.shiftType] : null,
                selection.workLocation ? formatWorkLocation(selection.workLocation) : null,
                selection.stateCode,
                selectionOverrides(selection).join(' · ') || null,
              ].filter(Boolean);
              return (
                <li key={`${selection.date}:${index}`}>
                  <span className="font-semibold">{formatDate(selection.date)}</span>
                  {parts.length > 0 && (
                    <span className="text-content-secondary"> · {parts.join(' · ')}</span>
                  )}
                </li>
              );
            })}
        </ul>
      </details>
    </section>
  );
};

const offerVersion = (offer: ServiceOffer, openDates: boolean): NegotiationHistoryVersion => ({
  id: offer.id,
  version: offer.version,
  sender: offer.sender,
  createdAt: offer.createdAt ?? null,
  headline: {
    shifts: offerShifts(offer),
    rate: offerRate(offer),
    value: offer.terms.totalContractValue == null ? null : offerValue(offer),
  },
  terms: offer.terms,
  extra: <ServiceAvailabilitySummary offer={offer} open={openDates} />,
});

/**
 * Both parties' negotiation card: rounds, the latest version's headline terms,
 * and the history of every version with its terms and selected availability.
 * A viewer who can accept starts with the current offer's terms open.
 */
export const ServiceNegotiationHistory = ({
  opportunity,
  reviewing = false,
}: {
  opportunity: ServiceOpportunity;
  reviewing?: boolean;
}) => {
  const offers = offersOf(opportunity);
  const latest = opportunity.latestNegotiation;
  const viewerSender = opportunity.viewerRole === 'RESPONDER' ? 'EMPLOYER' : 'PROVIDER';
  const versions = useMemo(
    () => offers.map((offer) => offerVersion(offer, reviewing && offer.id === latest?.id)),
    [offers, latest?.id, reviewing]
  );
  return (
    <div className="space-y-3">
      {latest?.selectionStatus === 'UNAVAILABLE' && <ServiceAvailabilitySummary offer={latest} />}
      {/* [MCV-1794] NegotiationHistory seeds its expanded state from these props on mount only, and
          the parent keeps this card mounted across refreshes. Without a key, a response that
          becomes acceptable after a refresh, or a newer acceptable offer, would show Accept over
          collapsed terms. Remounting on that change opens the offer being reviewed; browsing
          keeps one key so the viewer's own expand/collapse survives a refresh. */}
      <NegotiationHistory
        key={reviewing ? `review:${latest?.id ?? ''}` : 'browse'}
        rounds={offers.length}
        latest={
          latest && {
            version: latest.version,
            party: negotiationParty(latest.sender, viewerSender),
            headline: versions.find((entry) => entry.id === latest.id)?.headline ?? {},
          }
        }
        viewerSender={viewerSender}
        versions={versions}
        defaultExpanded={reviewing}
        defaultOpenLatest={reviewing}
        termGroups={JOB_TERM_GROUPS}
        termLabel={negotiationHistoryTermLabel}
        formatTerm={formatNegotiationHistoryTerm}
        emptyMessage={opportunity.status === 'MESSAGED' ? INQUIRY_NOTE : 'No negotiation started.'}
        cy={{
          card: 'service-negotiation-summary',
          toggle: 'btn-service-negotiation-history',
          row: 'service-negotiation-row',
          header: 'service-negotiation-table-header',
        }}
      />
    </div>
  );
};
